import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { useCreateClassMutation } from '~entities/classes/api/create-class';
import { getClassQueryOptions } from '~entities/classes/api/get-class';

import { DateTimePicker } from '~shared/components/date-time-picker';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '~shared/shadcn/ui/accordion';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~shared/shadcn/ui/alert-dialog';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';

import {
  buildCopyOfferingEditValues,
  type CopyOfferingEditErrors,
  type CopyOfferingEditValues,
  hasCopyOfferingEditErrors,
  isHourlyBillingCycle,
  toCopyOfferingFieldOverrides,
  validateCopyOfferingEditValues,
} from './copy-offering-edit-values';
import {
  copyOfferingDialogCopy,
  copyOfferings,
  type CopyOfferingSource,
  copyOfferingsToastMessage,
} from './copy-offerings';

type EditState = Record<string, CopyOfferingEditValues>;
type ErrorState = Record<string, CopyOfferingEditErrors>;

function updateField<K extends keyof CopyOfferingEditValues>(
  current: EditState,
  id: string,
  field: K,
  value: CopyOfferingEditValues[K],
): EditState {
  const existing = current[id];
  if (!existing) return current;
  return {
    ...current,
    [id]: { ...existing, [field]: value },
  };
}

function DateField({
  label,
  value,
  error,
  onChange,
}: {
  label: string;
  value: string;
  error?: string;
  onChange: (next: string) => void;
}) {
  return (
    <div className='space-y-2'>
      <Label>{label}</Label>
      <DateTimePicker
        hourCycle={24}
        granularity='day'
        displayFormat={{ hour24: 'PPP' }}
        value={value ? new Date(`${value}T00:00:00`) : undefined}
        onChange={(date) => {
          onChange(date ? format(date, 'yyyy-MM-dd') : '');
        }}
      />
      {error && <span className='text-sm text-destructive'>{error}</span>}
    </div>
  );
}

function FeeField({
  label,
  value,
  error,
  step = '0.01',
  onChange,
}: {
  label: string;
  value: string;
  error?: string;
  step?: string;
  onChange: (next: string) => void;
}) {
  return (
    <div className='space-y-2'>
      <Label>{label}</Label>
      <Input
        type='number'
        min={0}
        step={step}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        placeholder='0.00'
      />
      {error && <span className='text-sm text-destructive'>{error}</span>}
    </div>
  );
}

export function CopyOfferingDialog({
  open,
  onOpenChange,
  sources,
  onCopied,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sources: CopyOfferingSource[];
  onCopied?: () => void;
}) {
  const queryClient = useQueryClient();
  const { mutateAsync: createOffering } = useCreateClassMutation();
  const [step, setStep] = useState<'edit' | 'confirm'>('edit');
  const [isLoading, setIsLoading] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<EditState>({});
  const [errors, setErrors] = useState<ErrorState>({});
  const [openAccordionItems, setOpenAccordionItems] = useState<Array<string>>(
    [],
  );

  const copy = copyOfferingDialogCopy(sources.length);
  const sourceIdsKey = sources.map((source) => source.id).join(',');

  const resetState = useCallback(() => {
    setStep('edit');
    setIsLoading(false);
    setIsCopying(false);
    setLoadError(null);
    setEditValues({});
    setErrors({});
    setOpenAccordionItems([]);
  }, []);

  useEffect(() => {
    if (!open) {
      resetState();
      return;
    }

    let cancelled = false;

    const load = async () => {
      if (sources.length === 0) return;
      setIsLoading(true);
      setLoadError(null);
      setStep('edit');
      setErrors({});

      try {
        const offerings = await Promise.all(
          sources.map((source) =>
            queryClient.fetchQuery(getClassQueryOptions(source.id)),
          ),
        );
        if (cancelled) return;

        const nextValues: EditState = {};
        for (const offering of offerings) {
          nextValues[offering._id] = buildCopyOfferingEditValues(offering);
        }
        setEditValues(nextValues);
        setOpenAccordionItems(sources[0] ? [sources[0].id] : []);
      } catch (error) {
        if (cancelled) return;
        const reason =
          error instanceof Error ? error.message : 'Failed to load offerings';
        setLoadError(reason);
        toast.error(reason);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [open, sourceIdsKey, queryClient, resetState, sources]);

  const handleClose = (nextOpen: boolean) => {
    if (isCopying) return;
    onOpenChange(nextOpen);
  };

  const setField = <K extends keyof CopyOfferingEditValues>(
    id: string,
    field: K,
    value: CopyOfferingEditValues[K],
  ) => {
    setEditValues((current) => updateField(current, id, field, value));
    setErrors((current) => {
      const fieldName = field as keyof CopyOfferingEditErrors;
      if (!current[id]?.[fieldName]) return current;
      const nextOfferingErrors = { ...current[id] };
      delete nextOfferingErrors[fieldName];
      return { ...current, [id]: nextOfferingErrors };
    });
  };

  const handleContinue = () => {
    const nextErrors: ErrorState = {};
    let firstInvalidId: string | null = null;

    for (const source of sources) {
      const values = editValues[source.id];
      if (!values) {
        nextErrors[source.id] = { title: 'Offering details failed to load' };
        firstInvalidId ??= source.id;
        continue;
      }
      const fieldErrors = validateCopyOfferingEditValues(values);
      if (hasCopyOfferingEditErrors(fieldErrors)) {
        nextErrors[source.id] = fieldErrors;
        firstInvalidId ??= source.id;
      }
    }

    setErrors(nextErrors);
    if (firstInvalidId) {
      setOpenAccordionItems((current) =>
        current.includes(firstInvalidId)
          ? current
          : [...current, firstInvalidId],
      );
      toast.error('Please fix the highlighted fields before continuing.');
      return;
    }

    setStep('confirm');
  };

  const handleConfirm = async () => {
    if (sources.length === 0) return;

    const overridesById = Object.fromEntries(
      sources.map((source) => {
        const values = editValues[source.id];
        if (!values) {
          throw new Error(`Missing edit values for ${source.id}`);
        }
        return [source.id, toCopyOfferingFieldOverrides(values)];
      }),
    );

    setIsCopying(true);
    try {
      const result = await copyOfferings({
        sources,
        overridesById,
        fetchOffering: (id) => queryClient.fetchQuery(getClassQueryOptions(id)),
        createOffering,
      });

      if (result.copied === 0) {
        toast.error(copyOfferingsToastMessage(result));
        return;
      }

      onOpenChange(false);
      onCopied?.();
      if (result.failed > 0) {
        toast.error(copyOfferingsToastMessage(result));
        return;
      }
      toast.success(copyOfferingsToastMessage(result));
    } finally {
      setIsCopying(false);
    }
  };

  return (
    <>
      <Dialog open={open && step === 'edit'} onOpenChange={handleClose}>
        <DialogContent className='flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl sm:p-0'>
          <div className='shrink-0 px-4 pt-4'>
            <DialogHeader>
              <DialogTitle>
                {sources.length <= 1
                  ? 'Edit copy details'
                  : `Edit ${sources.length} copy details`}
              </DialogTitle>
              <DialogDescription>
                Update the name, dates, registration window, and tuition for
                each copy before publishing. Copies stay hidden until you unhide
                them.
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className='min-h-0 flex-1 overflow-y-auto px-4 py-3'>
            {isLoading ? (
              <div className='flex items-center gap-2 py-10 text-sm text-muted-foreground'>
                <Loader2 className='size-4 animate-spin' />
                Loading offering details...
              </div>
            ) : loadError ? (
              <p className='rounded-md border border-destructive p-3 text-sm text-destructive'>
                {loadError}
              </p>
            ) : (
              <Accordion
                type='multiple'
                value={openAccordionItems}
                onValueChange={setOpenAccordionItems}
                className='space-y-3'
              >
                {sources.map((source) => {
                  const values = editValues[source.id];
                  const fieldErrors = errors[source.id] ?? {};
                  if (!values) return null;
                  const hourly = isHourlyBillingCycle(
                    values.tuitionBillingCycle,
                  );

                  return (
                    <AccordionItem
                      key={source.id}
                      value={source.id}
                      className='rounded-lg border px-3'
                    >
                      <AccordionTrigger className='py-3 hover:no-underline'>
                        <div className='pr-2 text-left'>
                          <p className='text-sm font-medium'>{values.title}</p>
                          <p className='text-xs text-muted-foreground'>
                            From: {source.title}
                          </p>
                          {hasCopyOfferingEditErrors(fieldErrors) && (
                            <p className='text-xs text-destructive'>
                              Fix the highlighted fields
                            </p>
                          )}
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className='space-y-5 pb-2'>
                          <div className='space-y-2'>
                            <Label htmlFor={`copy-name-${source.id}`}>
                              Private Class Name
                            </Label>
                            <Input
                              id={`copy-name-${source.id}`}
                              value={values.title}
                              onChange={(event) => {
                                setField(
                                  source.id,
                                  'title',
                                  event.target.value,
                                );
                              }}
                            />
                            {fieldErrors.title && (
                              <span className='text-sm text-destructive'>
                                {fieldErrors.title}
                              </span>
                            )}
                          </div>

                          <div className='space-y-3'>
                            <h4 className='text-sm font-medium'>Dates</h4>
                            <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
                              <DateField
                                label='Start Date'
                                value={values.startDate}
                                error={fieldErrors.startDate}
                                onChange={(next) => {
                                  setField(source.id, 'startDate', next);
                                }}
                              />
                              <DateField
                                label='End Date'
                                value={values.endDate}
                                error={fieldErrors.endDate}
                                onChange={(next) => {
                                  setField(source.id, 'endDate', next);
                                }}
                              />
                            </div>
                            <DateField
                              label='Registration Date'
                              value={values.registrationStartDate}
                              error={fieldErrors.registrationStartDate}
                              onChange={(next) => {
                                setField(
                                  source.id,
                                  'registrationStartDate',
                                  next,
                                );
                              }}
                            />
                          </div>

                          {!hourly && (
                            <div className='space-y-3'>
                              <h4 className='text-sm font-medium'>Tuition</h4>
                              <div className='grid grid-cols-1 gap-3 sm:grid-cols-3'>
                                <FeeField
                                  label='Tuition'
                                  value={values.tuitionFee}
                                  error={fieldErrors.tuitionFee}
                                  onChange={(next) => {
                                    setField(source.id, 'tuitionFee', next);
                                  }}
                                />
                                <FeeField
                                  label='Membership'
                                  value={values.registrationFee}
                                  error={fieldErrors.registrationFee}
                                  step='1'
                                  onChange={(next) => {
                                    setField(
                                      source.id,
                                      'registrationFee',
                                      next,
                                    );
                                  }}
                                />
                                <FeeField
                                  label='Costume'
                                  value={values.costumeFee}
                                  error={fieldErrors.costumeFee}
                                  step='1'
                                  onChange={(next) => {
                                    setField(source.id, 'costumeFee', next);
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            )}
          </div>

          <DialogFooter className='shrink-0 gap-2 border-t px-4 py-3 sm:gap-0'>
            <Button
              type='button'
              variant='outline'
              disabled={isCopying}
              onClick={() => {
                handleClose(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type='button'
              disabled={isLoading || !!loadError || sources.length === 0}
              onClick={handleContinue}
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={open && step === 'confirm'}
        onOpenChange={(nextOpen) => {
          if (isCopying) return;
          if (!nextOpen) {
            setStep('edit');
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{copy.title}</AlertDialogTitle>
            <AlertDialogDescription>{copy.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={isCopying}
              onClick={(event) => {
                event.preventDefault();
                setStep('edit');
              }}
            >
              Back
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                void handleConfirm();
              }}
              disabled={isCopying || sources.length === 0}
            >
              {isCopying && <Loader2 className='animate-spin' />}
              {copy.confirm}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
